import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ApolloLink } from '@apollo/client';
import { Box, IconButton, InputBase, Typography } from '@mui/material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Pencil } from 'lucide-react';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { VersionHistoryIcon } from 'src/components/icons/VersionHistoryIcon';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import type { Thread } from '../../chat-helpers';

const useStyles = makeStyles()(theme => ({
  header: {
    fontSize: 10,
  },
  scrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(0.75),
    minWidth: 0,
    overflowY: 'auto',
  },
  threadActions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  threadContent: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'space-between',
    overflow: 'hidden',
    width: '100%',
  },
  threadContentLeft: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  threadIcon: {
    alignItems: 'center',
    display: 'flex',
  },
  threadItem: {
    '&:hover': {
      backgroundColor: theme.appColors.background,
    },
    border: theme.appBorders.outline,
    borderRadius: 12,
    cursor: 'pointer',
    display: 'flex',
    fontSize: 12,
    height: 40,
    justifyContent: 'flex-start',
    overflow: 'hidden',
    padding: theme.spacing(0, 1),
    width: '100%',
  },
  threadTextField: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
    zIndex: 1,
  },
  threadsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  timeText: {
    whiteSpace: 'nowrap',
  },
}));

export interface GroupedThreads {
  last7DaysThreads: Thread[];
  last30DaysThreads: Thread[];
  olderThreadsByMonth: Record<string, Thread[]>;
  todayThreads: Thread[];
}

export interface ChatEditorHistoryThreadsListProps {
  filteredGroupedThreads: GroupedThreads;
  onClickThread: (threadId: string) => void;
  /** Fires after a thread is deleted, so the host can leave a thread it was showing. */
  onThreadDeleted: (threadId: UUID) => void;
  totalThreads: number;
}

export function ChatEditorHistoryThreadsList({
  filteredGroupedThreads,
  onClickThread,
  onThreadDeleted,
  totalThreads,
}: ChatEditorHistoryThreadsListProps): JSX.Element {
  const { classes } = useStyles();

  if (totalThreads === 0) {
    return (
      <div className={classes.scrollContainer}>
        <Box
          sx={theme => ({
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            padding: theme.spacing(2),
          })}
        >
          <Typography align="center" variant="subtitle2">
            No chats found
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.scrollContainer}>
      {Boolean(filteredGroupedThreads.todayThreads.length) && (
        <RenderThreadGroup
          threads={filteredGroupedThreads.todayThreads}
          title="Today"
          onClickThread={onClickThread}
          onThreadDeleted={onThreadDeleted}
        />
      )}
      {Boolean(filteredGroupedThreads.last7DaysThreads.length) && (
        <RenderThreadGroup
          threads={filteredGroupedThreads.last7DaysThreads}
          title="Last 7 Days"
          onClickThread={onClickThread}
          onThreadDeleted={onThreadDeleted}
        />
      )}
      {Boolean(filteredGroupedThreads.last30DaysThreads.length) && (
        <RenderThreadGroup
          threads={filteredGroupedThreads.last30DaysThreads}
          title="Last 30 Days"
          onClickThread={onClickThread}
          onThreadDeleted={onThreadDeleted}
        />
      )}
      {Object.entries(filteredGroupedThreads.olderThreadsByMonth).map(([monthYear, threads]) => (
        <RenderThreadGroup
          key={monthYear}
          threads={threads}
          title={monthYear}
          onClickThread={onClickThread}
          onThreadDeleted={onThreadDeleted}
        />
      ))}
    </div>
  );
}

function RenderThreadGroup({
  onClickThread,
  onThreadDeleted,
  threads,
  title,
}: {
  onClickThread: (threadId: string) => void;
  onThreadDeleted: (threadId: UUID) => void;
  threads: Thread[];
  title?: string;
}) {
  const { classes } = useStyles();

  if (!threads.length) return null;

  return (
    <div className={classes.threadsContainer}>
      {title && (
        <Typography className={classes.header} color="textSecondary" variant="body2">
          {title}
        </Typography>
      )}
      {threads.map(thread => (
        <ThreadItem
          key={thread.id}
          thread={thread}
          onClickThread={onClickThread}
          onThreadDeleted={onThreadDeleted}
        />
      ))}
    </div>
  );
}

function ThreadItem({
  onClickThread,
  onThreadDeleted,
  thread,
}: {
  onClickThread: (threadId: string) => void;
  onThreadDeleted: (threadId: UUID) => void;
  thread: Thread;
}) {
  const { classes } = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [editedTitle, setEditedTitle] = useState(thread.title);
  const [updateThreadTitle, { loading: updatingTitle }] =
    Apollo.useUpdateAgentThreadTitleMutation();

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.select();
  }, [isEditing]);

  const handleSave = useCallback(async () => {
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) {
      setEditedTitle(thread.title);
      setIsEditing(false);
      return;
    }

    await updateThreadTitle({
      optimisticResponse: {
        __typename: 'Mutation',
        updateAgentThreadTitle: {
          __typename: 'AgentThread',
          id: thread.id,
          title: trimmedTitle,
        },
      },
      variables: {
        threadId: thread.id,
        title: trimmedTitle,
      },
    });
    setIsEditing(false);
    setEditedTitle(trimmedTitle);
  }, [editedTitle, thread.id, thread.title, updateThreadTitle]);

  const handleCancel = useCallback(() => {
    setEditedTitle(thread.title);
    setIsEditing(false);
  }, [thread.title]);

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          await handleSave();
          break;
        case 'Escape':
          e.preventDefault();
          handleCancel();
          break;
        default:
          break;
      }
    },
    [handleSave, handleCancel]
  );

  const handleEditClick = useCallback(
    (_e: React.MouseEvent) => {
      setEditedTitle(thread.title);
      setIsEditing(true);
    },
    [thread.title]
  );

  const handleThreadClick = useCallback(() => {
    if (!isEditing) onClickThread(thread.id);
  }, [isEditing, onClickThread, thread.id]);

  return (
    <div
      className={classes.threadItem}
      role="button"
      tabIndex={0}
      onClick={handleThreadClick}
      onKeyDown={e => {
        if (isEditing) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleThreadClick();
        }
      }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className={classes.threadContent}>
        <div className={classes.threadContentLeft}>
          <div className={classes.threadIcon}>
            <VersionHistoryIcon height={18} width={18} />
          </div>
          {isEditing ? (
            <InputBase
              className={classes.threadTextField}
              disabled={updatingTitle}
              inputRef={inputRef}
              value={editedTitle}
              onBlur={handleSave}
              onChange={e => setEditedTitle(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <Typography noWrap component="span">
              {thread.title}
            </Typography>
          )}
        </div>
        <ThreadActions
          createdAt={thread.createdAt}
          isEditing={isEditing}
          showActions={showActions}
          threadId={thread.id}
          onEditClick={handleEditClick}
          onThreadDeleted={onThreadDeleted}
        />
      </div>
    </div>
  );
}

function ThreadActions({
  createdAt,
  isEditing,
  onEditClick,
  onThreadDeleted,
  showActions,
  threadId,
}: {
  createdAt: string;
  isEditing: boolean;
  onEditClick: (e: React.MouseEvent) => void;
  onThreadDeleted: (threadId: UUID) => void;
  showActions: boolean;
  threadId: UUID;
}) {
  const { classes } = useStyles();
  const { deleteAgentThread, evictThread, loading: deleteLoading } = useDeleteThreads(threadId);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const { data } = await deleteAgentThread();
    setConfirmDeleteOpen(false);
    /**
     * `errorPolicy: 'all'` resolves failures instead of throwing, so a missing
     * `data.deleteAgentThread` means the mutation did not actually delete the
     * thread and the current selection must stay put.
     */
    if (!data?.deleteAgentThread) return;
    /**
     * The host is told before the cache entry goes, because it may be showing
     * this very thread. Evicted first, the open panel would read the thread as
     * missing and show "Chat not found" to the user who just deleted it. Both
     * updates land in the same render, so the host has already moved on.
     */
    onThreadDeleted(threadId);
    evictThread();
  };

  if (isEditing) return null;

  if (showActions) {
    return (
      <>
        <div className={classes.threadActions}>
          <AlineTooltip arrow placement="top" title="Rename chat thread">
            <div>
              <IconButton
                disabled={deleteLoading}
                size="small"
                style={{ padding: 6 }}
                onClick={e => {
                  e.stopPropagation();
                  onEditClick(e);
                }}
              >
                <Pencil size={16} />
              </IconButton>
            </div>
          </AlineTooltip>
          <AlineTooltip arrow placement="top" title="Delete chat thread">
            <div>
              <IconButton
                disabled={deleteLoading}
                size="small"
                style={{ padding: 6 }}
                onClick={handleDeleteClick}
              >
                <TrashcanIcon fontSize="small" />
              </IconButton>
            </div>
          </AlineTooltip>
        </div>
        <ConfirmationDialog
          message="Are you sure you want to delete this chat? This action cannot be undone."
          open={confirmDeleteOpen}
          primaryText="Delete"
          title="Delete chat?"
          onClick={e => e.stopPropagation()}
          onClickPrimary={handleConfirmDelete}
          onClose={() => setConfirmDeleteOpen(false)}
          onExit={() => setConfirmDeleteOpen(false)}
        />
      </>
    );
  }

  return (
    <Typography className={classes.timeText} color="textSecondary" variant="caption">
      {formatDistanceToNow(parseISO(createdAt), { addSuffix: true })}
    </Typography>
  );
}

/**
 * No optimistic eviction: removing the thread from the cache before the server
 * answers makes an open panel render "Chat not found" for the length of the
 * request. The caller evicts once the delete is confirmed and the host has
 * moved off the thread.
 */
export function useDeleteThreads(threadId: UUID): {
  deleteAgentThread: () => Promise<ApolloLink.Result<Apollo.DeleteAgentThreadMutation>>;
  evictThread: () => void;
  loading: boolean;
} {
  const [deleteAgentThread, { client, loading }] = Apollo.useDeleteAgentThreadMutation({
    variables: { threadId },
  });
  const safeDeleteAgentThread = useCallback(
    () => deleteAgentThread({ variables: { threadId } }),
    [deleteAgentThread, threadId]
  );
  const evictThread = useCallback(() => {
    client.cache.modify({
      fields: (_, { DELETE }) => DELETE,
      id: client.cache.identify({ __typename: 'AgentThread', id: threadId }),
    });
  }, [client, threadId]);

  return {
    deleteAgentThread: safeDeleteAgentThread,
    evictThread,
    loading,
  };
}
